import { useMemo } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { listItemMenu } from '@atlassian/jira-software-roadmap-timeline-table/src/common/constants.tsx';
import type { ListItemMenuOptions } from '@atlassian/jira-software-roadmap-timeline-table/src/common/types/item.tsx';
import {
	CHANGE_PARENT_ISSUE,
	SCHEDULING_SECTION,
	REMOVE_DATES,
	EDIT_DATES,
	EDIT_DEPENDENCY,
} from '../../ui/timeline-view/constants.tsx';
import { useTimelineFeatures } from '../features-context/index.tsx';
import messages from './messages.tsx';

export const useListItemMenuOptions = () => {
	const isLinkingEnabled = useIsLinkingEnabled();
	const { hasScheduleIssues, hasIssueLinking } = useTimelineFeatures();

	const {
		ITEMS_SECTION,
		MOVE_ITEM,
		CREATE_ITEM,
		CREATE_CHILD_ITEM,
		CREATE_ITEM_ABOVE,
		MOVE_ITEM_UP,
		MOVE_ITEM_DOWN,
		MOVE_ITEM_TO_TOP,
		MOVE_ITEM_TO_BOTTOM,
		SELECTED_ITEM_COUNTER,
	} = listItemMenu;

	const overrideLabels = useMemo(
		() => ({
			[ITEMS_SECTION]: messages[ITEMS_SECTION],
			[CREATE_ITEM]: messages[CREATE_ITEM],
			[MOVE_ITEM]: messages[MOVE_ITEM],
			[CREATE_CHILD_ITEM]: messages[CREATE_CHILD_ITEM],
			[CREATE_ITEM_ABOVE]: messages[CREATE_ITEM_ABOVE],
			[MOVE_ITEM_UP]: messages[MOVE_ITEM_UP],
			[MOVE_ITEM_DOWN]: messages[MOVE_ITEM_DOWN],
			[MOVE_ITEM_TO_TOP]: messages[MOVE_ITEM_TO_TOP],
			[MOVE_ITEM_TO_BOTTOM]: messages[MOVE_ITEM_TO_BOTTOM],
			[SELECTED_ITEM_COUNTER]: messages[SELECTED_ITEM_COUNTER],
		}),
		[
			CREATE_CHILD_ITEM,
			CREATE_ITEM,
			CREATE_ITEM_ABOVE,
			ITEMS_SECTION,
			MOVE_ITEM,
			MOVE_ITEM_DOWN,
			MOVE_ITEM_TO_BOTTOM,
			MOVE_ITEM_TO_TOP,
			MOVE_ITEM_UP,
			SELECTED_ITEM_COUNTER,
		],
	);

	const schedulingOptions = useMemo(
		() => [
			...(hasScheduleIssues && fg('jwm_timeline_meatballs_date_update')
				? [{ id: EDIT_DATES }, { id: REMOVE_DATES }]
				: []),
			...(hasIssueLinking && isLinkingEnabled && fg('jwm_timeline_meatballs_edit_dependency')
				? [{ id: EDIT_DEPENDENCY }]
				: []),
		],
		[hasIssueLinking, hasScheduleIssues, isLinkingEnabled],
	);

	const listItemMenuOptions: ListItemMenuOptions = useMemo(
		() => ({
			overrideLabels,
			customSections: [
				{
					id: ITEMS_SECTION,
					title: messages[ITEMS_SECTION],
					options: [
						...(fg('jwm_timeline_meatball_reparent_menu') ? [{ id: CHANGE_PARENT_ISSUE }] : []),
					],
				},
				...(schedulingOptions.length > 0
					? [
							{
								id: SCHEDULING_SECTION,
								title: messages[SCHEDULING_SECTION],
								options: schedulingOptions,
							},
						]
					: []),
			],
			customSectionsBulk: [],
		}),
		[ITEMS_SECTION, overrideLabels, schedulingOptions],
	);

	return listItemMenuOptions;
};
